import { type WritableDraft } from 'immer';

import { applyPropsPatch, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import {
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type RowClickActions,
} from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import {
  getInitialProps,
  patchTitle,
  toEngineActionsDisplayMode,
  toEngineBulkActionsDisplayMode,
} from '../../../ai';

import { toEnginePagination } from './table.appearance';
import { type TableResolvedContext } from './table.definition';

export interface ResolvedRowClickProps {
  enabled: boolean;
  action: RowClickActions;
}

export function buildTableProps(
  context: TableResolvedContext,
  draft: WritableDraft<EngineState>,
  unifiedRowActions: PropertiesConfigInputContainerItemUnifiedRowActionModel[],
  unifiedBulkActions: PropertiesConfigInputContainerItemUnifiedBulkActionModel[],
  resolvedRowClick?: ResolvedRowClickProps,
): PartialPropsOf<'table'> {
  let props: PartialPropsOf<'table'> = getInitialProps<'table'>(draft, context.element.id, {
    content: {},
  });

  const pagination = toEnginePagination(context.element.pageSize);

  props = applyPropsPatch(props, {
    content: {
      table: {
        usePagination: pagination.usePagination,
      },
      pagination: {
        enabled: pagination.usePagination,
        ...(pagination.pageSize !== undefined ? { count: pagination.pageSize } : {}),
      },
    },
  });

  props = patchTitle<'table'>(props, context.element.title, context.element.showTitle);

  props = applyPropsPatch(props, {
    content: {
      table: {
        showColumnHeaders: context.element.showColumnHeaders,
      },
    },
  });

  if (context.element.rowActionsDisplayMode) {
    props = applyPropsPatch(props, {
      content: {
        table: {
          actionsDisplayMode: toEngineActionsDisplayMode(context.element.rowActionsDisplayMode),
        },
      },
    });
  }

  if (context.element.bulkActionsDisplayMode) {
    props = applyPropsPatch(props, {
      content: {
        table: {
          bulkActionsDisplayMode: toEngineBulkActionsDisplayMode(
            context.element.bulkActionsDisplayMode,
          ),
        },
      },
    });
  }

  if (context.element.search !== undefined) {
    props = applyPropsPatch(props, {
      content: {
        searchAndFilters: {
          searchEnabled: context.element.search,
        },
      },
    });
  }

  if (unifiedRowActions.length > 0) {
    props = applyPropsPatch(props, {
      content: {
        unifiedRowActions,
      },
    });
  }

  if (unifiedBulkActions.length > 0) {
    props = applyPropsPatch(props, {
      content: {
        unifiedBulkActions,
      },
    });
  }

  if (resolvedRowClick) {
    props = applyPropsPatch(props, {
      content: {
        rowClick: {
          enabled: resolvedRowClick.enabled,
          action: resolvedRowClick.action,
        },
      },
    });
  }

  if (context.element.addNewRow) {
    props = applyPropsPatch(props, {
      content: {
        itemAdd: {
          enabled: true,
          label: context.element.addNewRow.label || '',
        },
      },
    });
  }

  return props;
}
